import { useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { TreeDeciduous, Loader2, ArrowRight, Sparkles, LogIn } from "lucide-react";

// -----------------------------------------------------------------------------
// FamilyTreeAutoLink — a one-click linker rendered inside every memorial
// hero. If the memorial is not yet linked to a family tree:
//   • Signed-in user  → hits POST /api/memorials/{slug}/auto-link-tree
//     which either finds the user's existing tree OR auto-creates one
//     named after them, links this memorial, and mints every relative
//     in family_details as Persons in the tree.
//   • Signed-out user → shown a soft prompt to sign in first.
// If the memorial is already linked, we render a compact "View in Family
// Tree" pill pointing at /family-trees/{slug} instead.
// -----------------------------------------------------------------------------

export default function FamilyTreeAutoLink({ m, user, onLinked }) {
  const [busy, setBusy] = useState(false);
  const linked = !!(m?.family_tree_slug || m?.family_tree_id);

  const linkNow = async () => {
    if (!user?.id) {
      toast.info("Please sign in first to connect this memorial to your family tree.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post(`/memorials/${m.slug || m.id}/auto-link-tree`);
      const treeName = data?.tree?.name || "your family tree";
      const added = data?.mint_stats?.persons_added ?? data?.mint_stats?.added ?? 0;
      toast.success(
        added > 0
          ? `Linked to ${treeName} — ${added} relative${added > 1 ? "s" : ""} added.`
          : `Linked to ${treeName}. You can now view it in Family Tree.`
      );
      onLinked?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not link to your family tree.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  // Already linked → show the "View in Family Tree" pill.
  if (linked) {
    return (
      <div
        className="mt-5 flex items-center gap-2 flex-wrap"
        data-testid="mem-family-tree-linked"
      >
        <Link
          to={`/family-trees/${m.family_tree_slug || m.family_tree_id}`}
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors"
          style={{
            background: "rgba(16, 185, 129, 0.12)",
            color: "#059669",
            border: "1px solid rgba(16, 185, 129, 0.35)",
          }}
        >
          <TreeDeciduous className="w-3.5 h-3.5" />
          Linked to a family tree
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  // Not signed in → prompt.
  if (!user?.id) {
    return (
      <div
        className="mt-5 rounded-xl border p-3 flex items-center gap-3 flex-wrap"
        data-testid="mem-family-tree-signin-prompt"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        <TreeDeciduous className="w-4 h-4" style={{ color: "#059669" }} />
        <div className="text-xs flex-1 min-w-[180px]" style={{ color: "var(--wofy-text-soft)" }}>
          Sign in to connect <strong>{m.preferred_name || m.full_name}</strong> to your family tree with one click.
        </div>
        <Link
          to="/auth/login"
          data-testid="mem-family-tree-signin-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5"
          style={{
            background: "rgba(139, 92, 246, 0.1)",
            color: "#7C3AED",
            border: "1px solid rgba(139, 92, 246, 0.3)",
          }}
        >
          <LogIn className="w-3.5 h-3.5" /> Sign in
        </Link>
      </div>
    );
  }

  // Signed-in, not linked → the actual button.
  return (
    <div className="mt-5" data-testid="mem-family-tree-linker">
      <button
        type="button"
        onClick={linkNow}
        disabled={busy}
        data-testid="mem-family-tree-link-btn"
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all hover:scale-[1.02]"
        style={{
          background: "linear-gradient(90deg, #059669 0%, #10B981 100%)",
          color: "#fff",
          opacity: busy ? 0.7 : 1,
        }}
        title="Auto-link this memorial to your family tree and add every relative in one click"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TreeDeciduous className="w-4 h-4" />}
        Connect to my Family Tree
        <Sparkles className="w-3.5 h-3.5 opacity-80" />
      </button>
      <div className="mt-1.5 text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
        One click adds {m.preferred_name || m.full_name} + every relative into your tree.
      </div>
    </div>
  );
}
